<template>
  <div
    class="relative flex items-start min-h-screen w-full max-w-full overflow-x-clip bg-gradient-to-b from-slate-50 to-white dark:from-slate-900 dark:to-slate-950"
  >
    <AdminSidebar
      :is-collapsed="isSidebarCollapsed"
      :is-open-mobile="isSidebarOpenMobile"
      :sidebar-search="sidebarSearch"
      :lang="lang"
      :t="t"
      @update:sidebar-search="sidebarSearch = $event"
      @update:is-open-mobile="isSidebarOpenMobile = $event"
    />

    <div class="flex flex-col flex-1 min-w-0 w-full">
      <AdminTopbar
        :is-collapsed="isSidebarCollapsed"
        :lang="lang"
        :t="t"
        @toggle-sidebar="toggleSidebar"
      />

      <main class="flex-1 px-4 py-6 md:px-6 lg:px-8">
        <div class="w-full mx-auto space-y-5 max-w-7xl">
          <!-- Hero header -->
          <section
            class="relative overflow-hidden border shadow-sm rounded-2xl bg-white/80 dark:bg-slate-900/80 border-slate-200/80 dark:border-slate-700/80 backdrop-blur"
          >
            <div
              class="pointer-events-none absolute -top-10 -end-8 w-36 h-36 rounded-[60px] bg-primary/15 blur-2xl"
            ></div>
            <div
              class="pointer-events-none absolute -bottom-8 start-0 w-32 h-32 rounded-full bg-secondary/15 blur-2xl"
            ></div>

            <div class="relative z-10 p-4 md:p-5">
              <RouterLink
                to="/tickets/my"
                class="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
              >
                <i
                  class="mdi mdi-arrow-right text-[16px]"
                  :class="lang === 'ar' ? '' : 'rotate-180'"
                ></i>
                {{ t("ticket_back_list") }}
              </RouterLink>

              <div
                class="flex flex-wrap items-start justify-between gap-4 mt-3"
              >
                <div class="min-w-0">
                  <div class="flex flex-wrap items-center gap-2">
                    <span
                      class="flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10 text-primary shrink-0"
                    >
                      <i class="mdi mdi-ticket-plus-outline text-[22px]"></i>
                    </span>
                    <div>
                      <h1
                        class="text-base font-bold text-slate-900 md:text-lg dark:text-white"
                      >
                        {{ t("ticket_create_title") }}
                      </h1>
                      <p class="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                        {{ t("ticket_create_subtitle") }}
                      </p>
                    </div>
                  </div>
                </div>

                <RouterLink
                  to="/tickets/my"
                  class="px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-200 text-slate-700 bg-white hover:bg-slate-50 dark:bg-slate-900 dark:text-slate-200 dark:border-slate-700 dark:hover:bg-slate-800"
                >
                  {{ t("ticket_back_list") }}
                </RouterLink>
              </div>
            </div>
          </section>

          <div class="grid grid-cols-1 gap-4 lg:grid-cols-3 lg:items-start">
            <!-- Form -->
            <form
              class="space-y-4 lg:col-span-2"
              @submit.prevent="submit"
            >
              <section
                class="overflow-hidden border shadow-sm rounded-2xl bg-white/80 dark:bg-slate-900/80 border-slate-200 dark:border-slate-700 backdrop-blur"
              >
                <header
                  class="flex items-center gap-2 px-4 py-3 border-b border-slate-200/70 dark:border-slate-700/70 bg-slate-50/80 dark:bg-slate-800/50"
                >
                  <i class="mdi mdi-text-box-outline text-[18px] text-primary"></i>
                  <h2
                    class="text-xs font-semibold tracking-widest uppercase text-slate-600 dark:text-slate-300"
                  >
                    {{ t("ticket_create_form_section") }}
                  </h2>
                </header>

                <div class="p-4 md:p-5 space-y-5">
                  <!-- Subject -->
                  <div>
                    <label
                      class="flex items-center gap-2 mb-1.5 text-xs font-medium text-slate-700 dark:text-slate-200"
                    >
                      <i class="mdi mdi-format-title text-[16px] text-slate-400"></i>
                      {{ t("ticket_subject") }}
                      <span class="text-rose-500">*</span>
                    </label>
                    <input
                      v-model="form.subject"
                      type="text"
                      maxlength="255"
                      class="w-full h-10 px-3 text-sm border rounded-xl dark:bg-slate-900 border-slate-200 dark:border-slate-700 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
                      :placeholder="t('ticket_subject_placeholder')"
                      :disabled="isSubmitting"
                    />
                    <p v-if="errors.subject" class="mt-1 text-[10px] text-rose-500">
                      {{ errors.subject }}
                    </p>
                  </div>

                  <!-- Description -->
                  <div>
                    <label
                      class="flex items-center gap-2 mb-1.5 text-xs font-medium text-slate-700 dark:text-slate-200"
                    >
                      <i class="mdi mdi-text text-[16px] text-slate-400"></i>
                      {{ t("ticket_description") }}
                      <span class="text-rose-500">*</span>
                    </label>
                    <textarea
                      v-model="form.description"
                      rows="6"
                      class="w-full px-3 py-2.5 text-sm border rounded-xl resize-y dark:bg-slate-900 border-slate-200 dark:border-slate-700 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 leading-relaxed"
                      :placeholder="t('ticket_description_placeholder')"
                      :disabled="isSubmitting"
                    ></textarea>
                    <p
                      v-if="errors.description"
                      class="mt-1 text-[10px] text-rose-500"
                    >
                      {{ errors.description }}
                    </p>
                  </div>

                  <!-- Category -->
                  <div
                    class="p-3.5 border rounded-xl border-slate-200/80 dark:border-slate-700/80 bg-slate-50/60 dark:bg-slate-800/40"
                  >
                    <label
                      class="flex items-center gap-2 mb-3 text-xs font-medium text-slate-700 dark:text-slate-200"
                    >
                      <i class="mdi mdi-shape-outline text-[16px] text-secondary"></i>
                      {{ t("ticket_category") }}
                    </label>
                    <fieldset
                      :disabled="isSubmitting"
                      class="p-0 m-0 border-0 min-w-0 disabled:opacity-60"
                    >
                      <TicketFilterPills
                        :pills="categoryPills"
                        :active-key="form.category_id"
                        :aria-label="t('ticket_category')"
                        @update:active-key="form.category_id = $event"
                      />
                    </fieldset>
                  </div>

                  <!-- Attachments — unchanged -->
                  <TicketFileUpload
                    v-model="form.attachments"
                    :label="t('ticket_attachments')"
                    :hint="t('ticket_attachments_hint')"
                    :add-label="lang === 'ar' ? 'إضافة ملفات' : 'Add files'"
                    :disabled="isSubmitting"
                    @error="showToast($event, 'error')"
                  />
                </div>

                <footer
                  class="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-slate-200/70 dark:border-slate-700/70 bg-slate-50/60 dark:bg-slate-800/40"
                >
                  <RouterLink
                    to="/tickets/my"
                    class="text-xs font-medium text-slate-500 hover:text-primary transition-colors"
                  >
                    {{ lang === "ar" ? "إلغاء" : "Cancel" }}
                  </RouterLink>
                  <button
                    type="submit"
                    class="inline-flex items-center gap-1.5 px-5 py-2.5 text-xs font-semibold text-white rounded-xl bg-primary hover:bg-primary/90 shadow-sm shadow-primary/25 disabled:opacity-60 transition-all active:scale-[0.98]"
                    :disabled="isSubmitting"
                  >
                    <i
                      v-if="isSubmitting"
                      class="mdi mdi-loading mdi-spin text-[16px]"
                    ></i>
                    <i v-else class="mdi mdi-send text-[16px]"></i>
                    {{ t("ticket_submit_create") }}
                  </button>
                </footer>
              </section>
            </form>

            <!-- Sidebar tips -->
            <aside class="space-y-4">
              <section
                class="p-5 border shadow-sm bg-white/80 dark:bg-slate-900/80 border-slate-200 dark:border-slate-700 rounded-2xl backdrop-blur"
              >
                <h3
                  class="flex items-center gap-2 text-xs font-semibold tracking-widest uppercase text-slate-500 dark:text-slate-300"
                >
                  <i class="mdi mdi-lightbulb-on-outline text-[16px] text-secondary"></i>
                  {{ t("ticket_create_tips_title") }}
                </h3>

                <ul class="mt-4 space-y-3">
                  <li class="flex gap-3 text-[12px] leading-relaxed text-slate-600 dark:text-slate-300">
                    <span
                      class="flex items-center justify-center w-6 h-6 shrink-0 rounded-lg bg-primary/10 text-primary"
                    >
                      <i class="mdi mdi-format-title text-[14px]"></i>
                    </span>
                    <span>{{ t("ticket_create_tip_subject") }}</span>
                  </li>
                  <li class="flex gap-3 text-[12px] leading-relaxed text-slate-600 dark:text-slate-300">
                    <span
                      class="flex items-center justify-center w-6 h-6 shrink-0 rounded-lg bg-primary/10 text-primary"
                    >
                      <i class="mdi mdi-text-long text-[14px]"></i>
                    </span>
                    <span>{{ t("ticket_create_tip_details") }}</span>
                  </li>
                  <li class="flex gap-3 text-[12px] leading-relaxed text-slate-600 dark:text-slate-300">
                    <span
                      class="flex items-center justify-center w-6 h-6 shrink-0 rounded-lg bg-secondary/15 text-secondary"
                    >
                      <i class="mdi mdi-paperclip text-[14px]"></i>
                    </span>
                    <span>{{ t("ticket_create_tip_files") }}</span>
                  </li>
                </ul>
              </section>

              <section
                class="p-4 border rounded-2xl border-primary/20 bg-primary/5 dark:bg-primary/10"
              >
                <p class="text-[11px] leading-relaxed text-slate-600 dark:text-slate-300">
                  <i class="mdi mdi-information-outline text-primary me-1"></i>
                  {{ t("ticket_attachments_hint") }}
                </p>
              </section>
            </aside>
          </div>
        </div>
      </main>
    </div>

    <Toast
      :show="toast.show"
      :type="toast.type"
      :message="toast.message"
      @update:show="toast.show = $event"
    />
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import AdminSidebar from "@/components/Auth/Sidebar.vue";
import AdminTopbar from "@/components/Auth/TopNav.vue";
import Toast from "@/components/Toast.vue";
import TicketFilterPills from "@/components/tickets/TicketFilterPills.vue";
import TicketFileUpload from "@/components/tickets/TicketFileUpload.vue";
import { ticketsApi } from "@/api/tickets";
import { useTicketMetaStore } from "@/stores/ticketMeta";
import { usePageI18n } from "@/composables/usePageI18n";
import { useAuthShell } from "@/composables/useAuthShell";
import { pickTranslatedName, categoryPillIcon } from "@/utils/ticket";

const { lang, t } = usePageI18n();
const {
  isSidebarCollapsed,
  isSidebarOpenMobile,
  sidebarSearch,
  toggleSidebar,
  toast,
  showToast,
} = useAuthShell();

const router = useRouter();
const ticketMeta = useTicketMetaStore();

const DEFAULT_CATEGORY_ID = "4";

const isSubmitting = ref(false);
const form = reactive({
  subject: "",
  description: "",
  // priority_id: "",
  category_id: DEFAULT_CATEGORY_ID,
  attachments: [],
});
const errors = reactive({ subject: "", description: "" });

/*
const priorityOptions = computed(() =>
  ticketMeta.priorities.map((p) => ({
    value: String(p.id),
    label: pickTranslatedName(p, lang.value),
  })),
);
*/

const categoryPills = computed(() =>
  [...ticketMeta.categories]
    .sort((a, b) => {
      if (String(a.id) === DEFAULT_CATEGORY_ID) return -1;
      if (String(b.id) === DEFAULT_CATEGORY_ID) return 1;
      return 0;
    })
    .map((c) => ({
      key: String(c.id),
      label: pickTranslatedName(c, lang.value),
      icon: categoryPillIcon(c),
    })),
);

/*
const slaHint = computed(() => {
  const code = String(
    ticketMeta.priorities.find((p) => String(p.id) === String(form.priority_id))?.code ||
      "",
  ).toLowerCase();
  if (code === "urgent" || code === "high") return "4";
  if (code === "medium" || code === "low") return "8";
  return "";
});
*/

function validate() {
  errors.subject = "";
  errors.description = "";
  if (!form.subject.trim()) {
    errors.subject = lang.value === "ar" ? "مطلوب" : "Required";
  }
  if (!form.description.trim()) {
    errors.description = lang.value === "ar" ? "مطلوب" : "Required";
  }
  return !errors.subject && !errors.description;
}

async function submit() {
  if (!validate() || isSubmitting.value) return;
  /*
  if (!form.priority_id) {
    showToast(
      lang.value === "ar" ? "اختر الأولوية" : "Select priority",
      "error",
    );
    return;
  }
  */

  isSubmitting.value = true;
  try {
    const payload = {
      subject: form.subject.trim(),
      description: form.description.trim(),
      // priority_id: Number(form.priority_id),
      attachments: form.attachments,
    };
    if (form.category_id) payload.category_id = Number(form.category_id);

    const res = await ticketsApi.create(payload);
    const ticketNumber =
      res.data?.ticket_number || res.data?.data?.ticket_number;

    showToast(res.message || t("ticket_create_success"));
    if (ticketNumber) {
      router.push({
        name: "tickets.show",
        params: { ticketNumber },
      });
    } else {
      router.push({ name: "tickets.my" });
    }
  } catch (e) {
    console.error(e);
    showToast(
      e?.response?.data?.message || t("ticket_create_failed"),
      "error",
    );
  } finally {
    isSubmitting.value = false;
  }
}

onMounted(async () => {
  await ticketMeta.fetchMetadataOnce();
  /*
  if (ticketMeta.priorities.length) {
    form.priority_id = String(
      ticketMeta.priorities.find((p) => p.code === "medium")?.id ||
        ticketMeta.priorities[0].id,
    );
  }
  */
});
</script>
